import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useDialog } from './DialogContext';
import axios from 'axios';
import PromptModal from './PromptModal';
import CreateUserModal from './CreateUserModal';
import {
  ADMIN_GROUP_LABEL,
  ONE_TIME_ROLE,
  canManageTeam,
  canManageCredentials,
  canDeleteUser,
  isOneTime,
} from './roles';
import FlameIcon from './FlameIcon';
import { API_BASE_URL } from './config';
import { CARD, PRIMARY_BTN } from './ui';
import UserRow from './team/UserRow';
import AdminGroup from './team/AdminGroup';
import CompanySection from './team/CompanySection';
import OneTimeSection from './team/OneTimeSection';

const sectionClass = `${CARD} overflow-hidden`;
const primaryBtn = PRIMARY_BTN;

const TeamPage = () => {
  const { user } = useAuth();
  const { confirm, alert } = useDialog();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  // Surveys are only needed to fill the create-user modal: a one-time account
  // is created against one, so the picker has to be populated before it opens.
  const [forms, setForms] = useState([]);
  // Who an inspector reports to: their manager, or the company's admins when
  // they have none. A list either way, because the admins are a group.
  const [supervisors, setSupervisors] = useState([]);
  const [passwords, setPasswords] = useState({});
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);

  const isManager = user.role === 'manager';
  const isAdmin = user.role === 'admin';
  const isDev = user.role === 'dev';
  // Only offer the reset where the API would allow it (own inspectors for a
  // manager, own company below admin for an admin, everyone for a dev).
  const canResetPassword = (member) => canManageCredentials(user, member);
  // Same idea for Remove: an admin outranks managers and inspectors but not
  // another admin, so the button has to disappear rather than 403.
  const canDelete = (member) => canDeleteUser(user, member);

  const fetchData = useCallback(async () => {
    try {
      if (user.role === 'inspector') {
        // One call: the API answers /api/users for an inspector with their own
        // team — their supervisor and their peers — so there is nothing left
        // here to filter or to ask for separately.
        const { data } = await axios.get(`${API_BASE_URL}/api/users`);
        setSupervisors(data.filter((u) => u.role === 'admin' || u.role === 'manager'));
        // Peers keep the current user among them, so they see themselves
        // tagged "You" rather than missing from their own team.
        setUsers(data.filter((u) => u.role === 'inspector'));
        return;
      }

      if (isManager) {
        // No role filter: the response has to carry this manager's one-time
        // accounts as well as their inspectors.
        const [usersRes, formsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/users?manager_id=${user.id}`),
          axios.get(`${API_BASE_URL}/api/forms`),
        ]);
        setUsers(usersRes.data);
        setForms(formsRes.data);
        return;
      }

      // admin + dev: backend scopes the result set appropriately.
      const [usersRes, companiesRes, formsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/users`),
        axios.get(`${API_BASE_URL}/api/companies`),
        axios.get(`${API_BASE_URL}/api/forms`),
      ]);
      setUsers(usersRes.data);
      setCompanies(companiesRes.data);
      setForms(formsRes.data);
    } catch (error) {
      console.error('Failed to fetch team:', error);
    }
  }, [user, isManager]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateUser = async (payload) => {
    if (payload.role === ONE_TIME_ROLE) {
      await handleCreateOneTime(payload);
      return;
    }
    try {
      const res = await axios.post(`${API_BASE_URL}/api/auth/register`, payload);
      // Shown inline right away; it also stays readable from the info button
      // next to the user until they set their own password.
      setPasswords((prev) => ({ ...prev, [res.data.id]: res.data.password }));
      setUserModalOpen(false);
      await fetchData();
    } catch (error) {
      console.error('Failed to add user:', error);
      alert({ title: 'Error', message: error.response?.data?.msg || 'Failed to add user' });
    }
  };

  // A batch can partly succeed — an address already in use doesn't stop the
  // other forty-nine — so the result is reported rather than assumed.
  const handleCreateOneTime = async (payload) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/auth/register-one-time`, payload);
      const { created = [], skipped = [] } = res.data;
      setPasswords((prev) => ({
        ...prev,
        ...Object.fromEntries(created.map((u) => [u.id, u.password])),
      }));
      setUserModalOpen(false);
      await fetchData();
      if (skipped.length) {
        alert({
          title: `Created ${created.length} of ${created.length + skipped.length}`,
          message: `Left out: ${skipped.map((s) => `${s.email} (${s.reason})`).join(', ')}`,
        });
      }
    } catch (error) {
      console.error('Failed to add one-time users:', error);
      const skipped = error.response?.data?.skipped || [];
      alert({
        title: 'Error',
        message: skipped.length
          ? `${error.response.data.msg}: ${skipped.map((s) => `${s.email} (${s.reason})`).join(', ')}`
          : error.response?.data?.msg || 'Failed to add one-time users',
      });
    }
  };

  const handleResetPassword = async (member) => {
    const ok = await confirm({
      title: 'Reset password',
      message: `Reset the password for ${member.email}? They'll get a new one-time password and must choose a new password at their next login.`,
      confirmLabel: 'Reset password',
    });
    if (!ok) return;
    try {
      const res = await axios.post(`${API_BASE_URL}/api/users/${member.id}/reset-password`);
      setPasswords((prev) => ({ ...prev, [member.id]: res.data.temp_password }));
      await fetchData();
    } catch (error) {
      console.error('Failed to reset password:', error);
      alert({ title: 'Error', message: error.response?.data?.msg || 'Failed to reset password' });
    }
  };

  const handleAddCompany = async (value) => {
    const name = value.trim();
    if (!name) return;
    setAddCompanyOpen(false);
    try {
      await axios.post(`${API_BASE_URL}/api/companies`, { name });
      await fetchData();
    } catch (error) {
      console.error('Failed to add company:', error);
      alert({ title: 'Error', message: error.response?.data?.msg || 'Failed to add company' });
    }
  };

  const handleDelete = async (member) => {
    const ok = await confirm({
      title: isOneTime(member.role) ? 'Delete one-time account' : 'Delete user',
      message: isOneTime(member.role)
        ? `Delete the one-time account for ${member.email}? The login stops working immediately. Anything already submitted is kept.`
        : `Are you sure you want to delete ${member.email}? This action is permanent and cannot be undone.`,
      confirmLabel: 'Delete user',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/users/${member.id}`);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete member:', error);
      alert({ title: 'Error', message: error.response?.data?.msg || 'Failed to delete member' });
    }
  };

  // Accounts the company loop below would never reach — no company, or one the
  // dev can't see. Small and usually empty, but silently dropping a user from
  // the only page that lists users is worse than an extra heading.
  const companyIds = new Set(companies.map((c) => c.id));
  const unassignedUsers = isDev ? users.filter((u) => !companyIds.has(u.company_id)) : [];

  const subtitle = {
    inspector: 'Your team and who you report to',
    manager: 'Manage your inspection team members',
    admin: 'Manage every manager and inspector across your company',
    dev: 'All users across every company',
  }[user.role];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-8">
      <CreateUserModal
        open={userModalOpen}
        actor={user}
        companies={companies}
        users={users}
        forms={forms}
        onConfirm={handleCreateUser}
        onCancel={() => setUserModalOpen(false)}
      />
      <PromptModal
        open={addCompanyOpen}
        title="Add company"
        message="Create a new company. Users and surveys can then be assigned to it."
        placeholder="e.g. Acme Industries"
        confirmLabel="Create company"
        required
        onConfirm={handleAddCompany}
        onCancel={() => setAddCompanyOpen(false)}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between mb-8">
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-bold text-white mb-2">Team</h1>
            <p className="text-lg text-gray-400">{subtitle}</p>
          </div>
          {canManageTeam(user.role) && (
            <div className="mt-4 md:mt-0 md:ml-4 flex gap-3">
              {isDev && (
                <button onClick={() => setAddCompanyOpen(true)} className={`${primaryBtn} from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600`}>
                  Add Company
                </button>
              )}
              <button onClick={() => setUserModalOpen(true)} className={primaryBtn}>
                {isManager ? 'Add Team Member' : 'Add User'}
              </button>
            </div>
          )}
        </div>

        {/* Who the inspector reports to. One manager, or the admins as a
            group — so the card names the group and lists them, rather than
            picking one admin and implying they are the manager. */}
        {user.role === 'inspector' && supervisors.length > 0 && (
          <div className="bg-gray-800/50 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6 mb-8">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-violet-500 rounded-lg mr-4 flex-shrink-0" />
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-white">
                  {user.manager_id ? 'Manager' : ADMIN_GROUP_LABEL}
                </h3>
                <p className="text-gray-400 truncate">
                  {supervisors.map((s) => s.email).join(', ')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Inspector + Manager: flat list. Manager is prepended so they see
            themselves at the top; inspector sees all coworkers including self. */}
        {(user.role === 'inspector' || isManager) && (() => {
          // One-time accounts are listed on their own below: they are guests
          // with a pass to one survey, not members of the team.
          const teamMembers = users.filter((u) => !isOneTime(u.role));
          const oneTimeAccounts = users.filter((u) => isOneTime(u.role));
          const displayList = isManager ? [user, ...teamMembers] : teamMembers;
          return (
            <>
            <div className={sectionClass}>
              {displayList.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <FlameIcon className="mx-auto w-16 h-16 mb-6" />
                  <h3 className="text-xl font-semibold text-white mb-2">No team members yet</h3>
                  <p className="text-gray-400 mb-6 max-w-sm mx-auto">
                    {isManager
                      ? 'Add team members to start collaborating on inspections.'
                      : 'Nobody else has been added to your team yet.'}
                  </p>
                  {isManager && (
                    <button onClick={() => setUserModalOpen(true)} className={primaryBtn}>
                      Add Your First Team Member
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="px-6 py-4 border-b border-gray-700">
                    <h3 className="text-lg font-semibold text-white">
                      {isManager ? 'Your Team' : 'Team Members'}
                    </h3>
                  </div>
                  <ul className="divide-y divide-gray-700">
                    {displayList.map((member) => (
                      <UserRow
                        key={member.id}
                        member={member}
                        password={passwords[member.id]}
                        onDelete={isManager ? handleDelete : undefined}
                        canDelete={canDelete}
                        onResetPassword={isManager ? handleResetPassword : undefined}
                        canResetPassword={canResetPassword}
                        showTempPassword={isManager}
                        currentUserId={user.id}
                      />
                    ))}
                  </ul>
                </>
              )}
            </div>
            {isManager && oneTimeAccounts.length > 0 && (
              <div className={`${sectionClass} mt-8`}>
                <OneTimeSection
                  accounts={oneTimeAccounts}
                  passwords={passwords}
                  onDelete={handleDelete}
                  canDelete={canDelete}
                  onResetPassword={handleResetPassword}
                  canResetPassword={canResetPassword}
                  showTempPasswords
                  currentUserId={user.id}
                />
              </div>
            )}
            </>
          );
        })()}

        {/* Admin: their own company's admin group — themselves, their
            managers with each manager's inspectors nested, and the inspectors
            who report to the admins directly. */}
        {isAdmin && (
          <div className={sectionClass}>
            <AdminGroup
              users={users}
              showCompany={false}
              passwords={passwords}
              onDelete={handleDelete}
              canDelete={canDelete}
              onResetPassword={handleResetPassword}
              canResetPassword={canResetPassword}
              showTempPasswords
              currentUserId={user.id}
            />
          </div>
        )}

        {/* Dev: one collapsible card per company, each holding that company's
            admin group. */}
        {isDev && (
          <div className="space-y-6">
            {companies.map((company) => (
              <CompanySection
                key={company.id}
                company={company}
                users={users.filter((u) => u.company_id === company.id)}
                passwords={passwords}
                onDelete={handleDelete}
                canDelete={canDelete}
                onResetPassword={handleResetPassword}
                canResetPassword={canResetPassword}
                showTempPasswords
                currentUserId={user.id}
              />
            ))}
            {unassignedUsers.length > 0 && (
              <CompanySection
                company={{ id: 'none', name: 'No company' }}
                users={unassignedUsers}
                passwords={passwords}
                onDelete={handleDelete}
                canDelete={canDelete}
                onResetPassword={handleResetPassword}
                canResetPassword={canResetPassword}
                showTempPasswords
                currentUserId={user.id}
              />
            )}
            {companies.length === 0 && unassignedUsers.length === 0 && (
              <p className="text-center text-gray-400 py-10">No companies yet.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamPage;
